import _, { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { getTimeZone, getMeridiemTimeFormat } from '../stores/SessionStore';

const msPerSeconds = 1000;
const msPerMinute = msPerSeconds * 60;
const msPerHour = msPerMinute * 60;
const msPerDay = msPerHour * 24;

function getDateTimeComponents(dateTime) {
    return {
        year: dateTime.getUTCFullYear(),
        monthOfYear: dateTime.getUTCMonth(),
        dayOfMonth: dateTime.getUTCDate(),
        dayOfWeek: dateTime.getUTCDay(),
        hourOfDay: dateTime.getUTCHours(),
        minuteOfHour: dateTime.getUTCMinutes(),
    };
}

function getGranularityOffset(granularityIncrement, value) {
    const remainder = value % granularityIncrement;
    return remainder > 0 ? granularityIncrement - remainder : 0;
}

const granularities = {
    oneMinute: {
        updateInterval: msPerMinute,
        realTimeUpdateInterval: msPerSeconds * 10,
        minimum: 0,
        maximum: msPerHour * 3,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(
                    components.year,
                    components.monthOfYear,
                    components.dayOfMonth,
                    components.hourOfDay,
                    components.minuteOfHour,
                    0,
                    0
                )
            );
        },
    },
    twoMinutes: {
        updateInterval: msPerMinute * 2,
        realTimeUpdateInterval: msPerSeconds * 30,
        minimum: msPerHour * 3,
        maximum: msPerHour * 6,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(
                    components.year,
                    components.monthOfYear,
                    components.dayOfMonth,
                    components.hourOfDay,
                    components.minuteOfHour + getGranularityOffset(2, components.minuteOfHour),
                    0,
                    0
                )
            );
        },
    },
    fiveMinutes: {
        updateInterval: msPerMinute * 5,
        realTimeUpdateInterval: msPerMinute,
        minimum: msPerHour * 6,
        maximum: msPerHour * 15,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(
                    components.year,
                    components.monthOfYear,
                    components.dayOfMonth,
                    components.hourOfDay,
                    components.minuteOfHour + getGranularityOffset(5, components.minuteOfHour),
                    0,
                    0
                )
            );
        },
    },
    tenMinutes: {
        updateInterval: msPerMinute * 10,
        realTimeUpdateInterval: msPerMinute * 2,
        minimum: msPerHour * 15,
        maximum: msPerHour * 30,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(
                    components.year,
                    components.monthOfYear,
                    components.dayOfMonth,
                    components.hourOfDay,
                    components.minuteOfHour + getGranularityOffset(10, components.minuteOfHour),
                    0,
                    0
                )
            );
        },
    },
    fifteenMinutes: {
        updateInterval: msPerMinute * 15,
        realTimeUpdateInterval: msPerMinute * 5,
        minimum: msPerHour * 30,
        maximum: msPerHour * 60,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(
                    components.year,
                    components.monthOfYear,
                    components.dayOfMonth,
                    components.hourOfDay,
                    components.minuteOfHour + getGranularityOffset(15, components.minuteOfHour),
                    0,
                    0
                )
            );
        },
    },
    oneHour: {
        updateInterval: msPerHour,
        realTimeUpdateInterval: msPerMinute * 10,
        minimum: msPerHour * 60,
        maximum: msPerDay * 8,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(components.year, components.monthOfYear, components.dayOfMonth, components.hourOfDay, 0, 0, 0)
            );
        },
    },
    twoHours: {
        updateInterval: msPerHour,
        realTimeUpdateInterval: msPerMinute * 10,
        minimum: msPerDay * 8,
        maximum: msPerDay * 16,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(
                    components.year,
                    components.monthOfYear,
                    components.dayOfMonth,
                    components.hourOfDay + getGranularityOffset(2, components.hourOfDay),
                    0,
                    0,
                    0
                )
            );
        },
    },
    threeHours: {
        updateInterval: msPerHour,
        realTimeUpdateInterval: msPerMinute * 10,
        minimum: msPerDay * 16,
        maximum: msPerDay * 24,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(
                    components.year,
                    components.monthOfYear,
                    components.dayOfMonth,
                    components.hourOfDay + getGranularityOffset(3, components.hourOfDay),
                    0,
                    0,
                    0
                )
            );
        },
    },
    fourHours: {
        updateInterval: msPerHour,
        realTimeUpdateInterval: msPerMinute * 10,
        minimum: msPerDay * 24,
        maximum: msPerDay * 32,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(
                    components.year,
                    components.monthOfYear,
                    components.dayOfMonth,
                    components.hourOfDay + getGranularityOffset(4, components.hourOfDay),
                    0,
                    0,
                    0
                )
            );
        },
    },
    sixHours: {
        updateInterval: msPerHour,
        realTimeUpdateInterval: msPerMinute * 10,
        minimum: msPerDay * 32,
        maximum: msPerDay * 48,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(
                    components.year,
                    components.monthOfYear,
                    components.dayOfMonth,
                    components.hourOfDay + getGranularityOffset(6, components.hourOfDay),
                    0,
                    0,
                    0
                )
            );
        },
    },
    twelveHours: {
        updateInterval: msPerHour,
        realTimeUpdateInterval: msPerMinute * 10,
        minimum: msPerDay * 48,
        maximum: msPerDay * 96,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(
                    components.year,
                    components.monthOfYear,
                    components.dayOfMonth,
                    components.hourOfDay + getGranularityOffset(12, components.hourOfDay),
                    0,
                    0,
                    0
                )
            );
        },
    },
    oneDay: {
        updateInterval: msPerHour,
        realTimeUpdateInterval: msPerMinute * 10,
        minimum: msPerDay * 96,
        maximum: msPerDay * 192,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(Date.UTC(components.year, components.monthOfYear, components.dayOfMonth, 0, 0, 0, 0));
        },
    },
    twoDays: {
        updateInterval: msPerHour,
        minimum: msPerDay * 192,
        maximum: msPerDay * 400,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(
                    components.year,
                    components.monthOfYear,
                    components.dayOfMonth + getGranularityOffset(2, components.dayOfMonth),
                    0,
                    0,
                    0,
                    0
                )
            );
        },
    },
    fourDays: {
        updateInterval: msPerHour,
        realTimeUpdateInterval: msPerMinute * 10,
        minimum: msPerDay * 400,
        maximum: msPerDay * 1600,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(
                    components.year,
                    components.monthOfYear,
                    components.dayOfMonth + getGranularityOffset(4, components.dayOfMonth),
                    0,
                    0,
                    0,
                    0
                )
            );
        },
    },
    oneWeek: {
        updateInterval: msPerHour,
        realTimeUpdateInterval: msPerMinute * 10,
        minimum: msPerDay * 1600,
        maximum: Number.MAX_SAFE_INTEGER,
        align(dateTime) {
            const components = getDateTimeComponents(dateTime);
            return new Date(
                Date.UTC(
                    components.year,
                    components.monthOfYear,
                    components.dayOfMonth + getGranularityOffset(7, components.dayOfWeek + 1),
                    0,
                    0,
                    0,
                    0
                )
            );
        },
    },
};

/**
 * Finds the best granularity for the specified time range.
 *
 * @param {Date} start The start of the time range.
 * @param {Date} end The end of the time range.
 *
 * @private
 */
function getGranularityForTimeRange(start, end) {
    const span = end.getTime() - start.getTime();
    return _.find(granularities, (granularity) => {
        return span > granularity.minimum && span <= granularity.maximum;
    });
}

/**
 * @param {Number} days The number of days in the duration.
 * @param {Number} hours The number of hours in the duration.
 * @param {Number} minutes The number of minutes in the duration.
 * @param {Number} seconds The number of seconds in the duration.
 * @param {Number} precision Max number of time granularities to show. Default = 1, overridden by showSeconds.
 * @param {Boolean} showSeconds Whether or not to show seconds in the duration.
 * @param {Boolean} surroundWithParens Whether to surround the formatted duration with parenthesis.
 *
 * @returns {String} The formatted duration.
 */
function formatDuration(days, hours, minutes, seconds, precision, showSeconds, surroundWithParens) {
    const durationParts = [];
    let count = 0;
    [
        [days, 'd'],
        [hours, 'h'],
        [minutes, 'm'],
        [seconds, 's'],
    ].forEach((pair) => {
        const val = pair[0];
        const suffix = pair[1];
        if (val !== 0 && (showSeconds || (precision > 0 && count < precision))) {
            durationParts.push(`${val}${suffix}`);
        }
        if (durationParts.length) {
            count += 1;
        }
    });

    let durationFormatted = _.trim(durationParts.join(' '));

    if (surroundWithParens && !_.isEmpty(durationFormatted)) {
        durationFormatted = ` (${durationFormatted})`;
    }

    return durationFormatted;
}

/**
 * Align the start and end time with the best granularity for the range.
 *
 * @param {moment} start The start date and time of the time range.
 * @param {moment} end The end date and time of the time range.
 *
 * @returns {{start: moment, end: moment}} The aligned time range.
 */
export function alignTimeRangeWithGranularity(start, end) {
    const startDate = start.toDate();
    const endDate = end.toDate();
    const granularity = getGranularityForTimeRange(startDate, endDate);
    return {
        start: moment(granularity.align(startDate)),
        end: moment(granularity.align(endDate)),
    };
}

export function getAutoUpdateIntervalForTimeRange(timeRange, realTime = false) {
    const { start, end } = timeRange;
    const granularity = getGranularityForTimeRange(start, end);

    return realTime ? granularity.realTimeUpdateInterval : granularity.updateInterval;
}

/**
 * Pass in an epoch UTC date and have it converted to the passed in timezone
 */
export function convertFromEpochUTCTo(epochTime, toTimeZone) {
    if (epochTime) {
        return moment.unix(epochTime).tz(toTimeZone);
    }

    return null;
}

/**
 * Pass in a UTC date and have it converted to the passed in timezone
 */
export function convertFromUTCTo(dateToConvert, toTimeZone) {
    if (dateToConvert) {
        return moment(dateToConvert).tz(toTimeZone);
    }

    return null;
}

/**
 * Convert a "last restarted at" time to an uptime duration string like "23 days, 1 hour, 12 minutes".
 * @param epochMillis
 */
export function getUpTimeDuration(epochMillis) {
    if (epochMillis <= 0) {
        return '--';
    }

    const duration = Math.floor((new Date().getTime() - epochMillis) / 1000);
    const days = Math.floor(duration / 86400);
    const hours = Math.floor((duration - days * 86400) / 3600);
    const minutes = Math.floor((duration - days * 86400 - hours * 3600) / 60);

    const daysUp = days > 0 ? `${days} day${days !== 1 ? 's' : ''}` : '';
    const hoursUp = daysUp || hours > 0 ? `${daysUp ? ' ' : ''}${hours} hour${hours !== 1 ? 's' : ''}` : '';
    const minutesUp = hoursUp || minutes > 0 ? `${hoursUp ? ' ' : ''}${minutes} minute${minutes !== 1 ? 's' : ''}` : '';

    return `${daysUp}${hoursUp}${minutesUp}`;
}

/**
 * Given a number of MS, this method returns a string like 1d 2h 5m, for the total time
 *   MDL - copied from chart.js...
 * @param differenceInMs
 * @param precision
 * @param showSeconds
 * @param surroundWithParens
 * @returns {string}
 */
export function getMSDuration(differenceInMs, precision, showSeconds, surroundWithParens = true) {
    let days;
    let hours;
    const DAY_IN_MS = 86400000;
    const HOUR_IN_MS = 3600000;
    const MIN_IN_MS = 60000;
    const SEC_IN_MS = 1000;

    // Only use Math.round() if the precision is effectively 1. Otherwise, the calculations
    // below can cause differenceInMs to go negative...
    const roundFunc = precision > 1 || showSeconds ? Math.floor : Math.round;

    days = roundFunc(differenceInMs / DAY_IN_MS);
    days = days < 1 ? 0 : Math.floor(days);
    if (days >= 1 && !showSeconds && precision < 2) {
        days = `${days}d`;
        return surroundWithParens ? ` (${days})` : days;
    }
    differenceInMs -= days * DAY_IN_MS;

    hours = roundFunc(differenceInMs / HOUR_IN_MS);
    hours = hours < 1 ? 0 : Math.floor(hours);
    if (hours >= 1 && !showSeconds && precision < 2) {
        hours = `${hours}h`;
        return surroundWithParens ? ` (${hours})` : hours;
    }
    differenceInMs -= hours * HOUR_IN_MS;

    const minutes = Math.floor(differenceInMs / MIN_IN_MS);
    differenceInMs -= minutes * MIN_IN_MS;

    const seconds = Math.floor(differenceInMs / SEC_IN_MS);

    return formatDuration(days, hours, minutes, seconds, precision, showSeconds, surroundWithParens);
}

/**
 * Return a formatted string of endTime-startTime. If endTime-startTime is less than one minute, show
 * "<1m" if showSeconds is false (default), or the number of seconds if showSeconds is true.
 * @param startTime Start time
 * @param endTime End time
 * @param {Number} precision Max number of time granularities to show. Default = 1, overridden by showSeconds.
 * @param {Boolean} showSeconds Whether or not to show seconds in the duration. Default = false.
 * @param {String} invalidValue Value to use for invalid startTime/endTime
 * @returns {string|*}
 *
 * Examples for endTime-startTime = 1d 4h 17m 36s
 *
 * precision    showSeconds    return_value
 *     1           false            1d
 *     2           false          1d 4h
 *     3           false        1d 4h 17m
 *     *           true       1d 4h 17m 36s
 */
export function durationBetween(startTime, endTime, precision = 1, showSeconds = false, invalidValue) {
    if (!startTime || !endTime) {
        return invalidValue;
    }

    const startMoment = moment(startTime).utc();
    const endMoment = moment(endTime).utc();
    const durationInMs = endMoment.valueOf() - startMoment.valueOf();

    if (durationInMs >= 60000 || showSeconds === true) {
        const durationString = getMSDuration(durationInMs, precision, showSeconds, false);
        return isEmpty(durationString) ? invalidValue : `${durationString}`;
    }

    return '<1m';
}

/**
 * @param {Date} date
 * @returns Date
 */
export function floorToMinute(date) {
    return moment(date).startOf('minute').toDate();
}

/**
 * Convert a time string from source timezone to user's timezone.
 * Takes a time string in "HH:mm" format and converts it to the user's timezone.
 * Uses current date + specified time in source timezone for conversion.
 *
 * @param {String} timeString Time string in "HH:mm" format (e.g., "14:30")
 * @param {String} fromTz Source timezone (default: 'UTC')
 * @param {String} format Output format string (default: 'HH:mm')
 * @returns {String} Formatted time string in user's timezone with abbreviation (e.g., "2:15 PM CST"), or empty string if input is null/empty/invalid
 */
export function convertTimeToUserTZ(timeString, fromTz = 'UTC', format = 'HH:mm') {
    if (!timeString || isEmpty(timeString)) {
        return '';
    }

    // Validate time string format
    const parts = timeString.split(':');
    if (parts.length !== 2) {
        return '';
    }
    const [hours, minutes] = parts;

    // Validate parsed time values
    const hourNum = parseInt(hours, 10);
    const minuteNum = parseInt(minutes, 10);
    if (_.isNaN(hourNum) || _.isNaN(minuteNum) || hourNum < 0 || hourNum > 23 || minuteNum < 0 || minuteNum > 59) {
        return '';
    }

    const userTz = getTimeZone();

    // Create a moment object with today's date and the specified time in source timezone
    const timeInSourceTz = moment.tz(fromTz).hours(hourNum).minutes(minuteNum).seconds(0);

    // Convert to user's timezone
    const timeInUserTz = timeInSourceTz.tz(userTz);

    // Get timezone abbreviation (e.g., "CST" instead of "Asia/Shanghai")
    const tzAbbr = timeInUserTz.zoneAbbr();

    // Format and return with timezone abbreviation
    return `${timeInUserTz.format(format)} ${tzAbbr}`;
}

/**
 * Get the user's preferred time format based on their 12/24-hour preference.
 * Returns 'h:mm A' for 12-hour format (with AM/PM) or 'HH:mm' for 24-hour format.
 *
 * @returns {String} Time format string ('h:mm A' for 12-hour, 'HH:mm' for 24-hour)
 */
export function getUserTimeFormat() {
    const useMeridiem = getMeridiemTimeFormat();
    return useMeridiem ? 'h:mm A' : 'HH:mm';
}
